<template>
  <div class="modal two-factor-auth-modal">
    <div class="overlay" @click="$emit('close')"></div>
    <div class="wrapper">
      <div class="form">
        <div class="form-wrapper">
          <img class="close" @click="$emit('close')" src="@/assets/icons/close.svg" />
          <div class="form-fields">
            <div class="form-fields-wrapper">
              <div class="title small">{{ $t('Two-Factor Authentication') }}</div>
              <p class="desc auth-description">
                {{ $t('A verification code has been sent to your email. Please enter it below to continue.') }}
              </p>
              
              <div class="verification-code-container">
                <input 
                  type="text" 
                  v-model="verificationCode" 
                  :maxlength="codeLength" 
                  ref="codeInput"
                  class="verification-code-input"
                  :placeholder="getCodePlaceholder()"
                  @input="onCodeInput"
                />
              </div>
              
              <div class="timer-container">
                <span v-if="countdown > 0">{{ $t('Code expires in') }} {{ formatTime(countdown) }}</span>
                <span class="resend-link" @click="resendCode">{{ $t('Resend code') }}</span>
              </div>
              
              <div class="cta-container">
                <button class="button" @click="verifyCode" :disabled="verificationCode.length !== codeLength">
                  <span>{{ $t('Verify') }}</span>
                </button>
              </div>
              
              <transition name="fade">
                <div v-if="error" class="error-desc desc red">{{ error }}</div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'TwoFactorAuthModal',
  props: {
    codeLength: {
      type: Number,
      default: 6
    },
    expiryMinutes: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      verificationCode: '',
      countdown: 0, // Will be set in mounted
      countdownInterval: null,
      error: null
    };
  },
  mounted() {
    this.countdown = this.expiryMinutes * 60; // Convert minutes to seconds
    this.startCountdown();
    this.$nextTick(() => {
      if (this.$refs.codeInput) {
        this.$refs.codeInput.focus();
      }
    });
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  methods: {
    ...mapActions('user', ['verify2FA', 'resend2FA']),
    
    getCodePlaceholder() {
      return '0'.repeat(this.codeLength);
    },
    startCountdown() {
      this.clearCountdown();
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.clearCountdown();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    },
    onCodeInput() {
      // Keep only numbers
      this.verificationCode = this.verificationCode.replace(/[^0-9]/g, '');
      
      if (this.verificationCode.length === this.codeLength) {
        this.verifyCode();
      }
    },
    async verifyCode() {
      if (this.verificationCode.length !== this.codeLength) {
        return;
      }
      
      try {
        this.error = null; // Clear any existing error
        const result = await this.verify2FA(this.verificationCode);
        
        if (result && result.success) {
          // Set a temporary success message
          this.error = null;
          this.$toast.success('Authentication successful!', {
            timeout: 1500
          });
          // Emit the verified event with a small delay to show success state
          setTimeout(() => {
            this.$emit('verified', result.userData || {});
          }, 300);
        } else {
          this.error = (result && result.error) || 'Invalid verification code';
        }
      } catch (err) {
        console.error('2FA verification error:', err);
        this.error = err.message || 'An error occurred. Please try again.';
      }
    },
    async resendCode() {
      try {
        this.error = null;
        const result = await this.resend2FA();
        
        if (result.success) {
          this.countdown = this.expiryMinutes * 60; // Reset to the expiry time
          this.startCountdown();
          this.verificationCode = '';
        } else {
          this.error = result.error || 'Could not resend code';
        }
      } catch (err) {
        this.error = err.message || 'Could not resend code. Please try again later.';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal.two-factor-auth-modal {
  .overlay {
    // Overlay styles
  }

  .wrapper {
    // Wrapper styles
    .form {
      // Form styles
      .form-wrapper {
        // Form wrapper styles
        .close {
          // Close button styles
        }

        .form-fields {
          // Form fields styles
          .form-fields-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            
            .title.small {
              margin-bottom: 20px;
            }
            
            .auth-description {
              margin-bottom: 24px;
              text-align: center;
              color: #88868d;
              max-width: 320px;
            }
            
            .verification-code-container {
              margin-bottom: 16px;
              width: 100%;
              max-width: 304px;
              
              .verification-code-input {
                width: 100%;
                height: 50px;
                background-color: transparent;
                border: 1px solid #626165;
                color: #fff;
                font-size: 20px;
                text-align: center;
                letter-spacing: 4px;
                
                &:focus {
                  outline: none;
                  border-color: #4a88ff;
                }
                
                &::placeholder {
                  color: #88868d;
                  letter-spacing: 2px;
                }
              }
            }
            
            .timer-container {
              display: flex;
              justify-content: center;
              gap: 16px;
              margin-bottom: 24px;
              color: #88868d;
              font-size: 14px;
              
              .resend-link {
                color: #4a88ff;
                cursor: pointer;
                
                &:hover {
                  opacity: 0.7;
                }
              }
            }
            
            .cta-container {
              width: 100%;
              max-width: 304px;
              display: flex;
              justify-content: center;
              
              .button {
                width: 100%;
                
                &[disabled] {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              }
            }
            
            .error-desc.desc.red {
              margin-top: 16px;
              color: #ff4a4a;
              text-align: center;
            }
          }
        }
      }
    }
  }
}
</style> 